import { IHierarchyItem, IItem, IItemProperty } from "../interfaces/IItem";

import {
  IViewConfigurationItem,
  IEditObjectConfiguration,
  IConfiguration,
  IObjectTypeConfigurationItem,
} from "../interfaces/configuration/IConfiguration";
import sortBy from "lodash/sortBy";
import trim from "lodash/trim";
import uniqBy from "lodash/uniqBy";
import { PropertyDataType } from "../constants/datatypes";
import { WORKFLOW_STATE_MFILES_PROPERTY_ID } from "../constants/common";

export const getProperties = (properties: IItemProperty[], propertyIds: number[]): IItemProperty[] => {
  return propertyIds.map((pid) => properties.find((p) => p.property === pid)).filter((p) => p != null);
};

export const getProperty = (properties: IItemProperty[], propertyId: number): IItemProperty | undefined => {
  return properties.find((p) => p.property === propertyId);
};

const getPropertyAsLookups = (properties: IItemProperty[], propertyId: number): number[] => {
  return properties.find((p) => p.property === propertyId)?.value?.split(",").map(s => parseInt(s)) ?? [];
};

export const isInLookupPropertyValue = (dataType: PropertyDataType, value: string, valueToCheck: string): boolean => {
  if (dataType === PropertyDataType.MultiSelectLookup) {
    const parts = value?.split(",").map(trim) || [];
    return parts.includes(valueToCheck);
  } else {
    return trim(value) === trim(valueToCheck);
  }
};

export const addToLookupPropertyValue = (dataType: PropertyDataType, oldValue: string, valueToAdd: string): string => {
  if (isInLookupPropertyValue(dataType, oldValue, valueToAdd)) {
    return oldValue;
  } else if (dataType === PropertyDataType.MultiSelectLookup) {
    const parts = oldValue?.split(",").map(trim) || [];
    return parts.concat(valueToAdd).join(",");
  } else {
    return trim(valueToAdd);
  }
};

export const addManyToLookupPropertyValue = (dataType: PropertyDataType, oldValue: string, valuesToAdd: string[]): string => {
  return valuesToAdd.reduce((accumulator, val) => addToLookupPropertyValue(dataType, accumulator, val), oldValue);
};

export const removeFromLookupPropertyValue = (dataType: PropertyDataType, oldValue: string, valueToRemove: string): string => {
  if (isInLookupPropertyValue(dataType, oldValue, valueToRemove) === false) {
    return oldValue;
  } else if (dataType === PropertyDataType.MultiSelectLookup) {
    const parts = oldValue?.split(",").map(trim) || [];
    const partsWithoutValueToRemove = parts.filter((part) => part !== valueToRemove);
    return partsWithoutValueToRemove.length > 0 ? partsWithoutValueToRemove.join(",") : null;
  } else {
    return null;
  }
};

export const removeManyFromLookupPropertyValue = (dataType: PropertyDataType, oldValue: string, valuesToRemove: string[]): string => {
  return valuesToRemove.reduce((accumulator, val) => removeFromLookupPropertyValue(dataType, accumulator, val), oldValue);
};

export const toggleInLookupPropertyValue = (dataType: PropertyDataType, oldValue: string, valueToToggle: string): string => {
  return isInLookupPropertyValue(dataType, oldValue, valueToToggle)
    ? removeFromLookupPropertyValue(dataType, oldValue, valueToToggle)
    : addToLookupPropertyValue(dataType, oldValue, valueToToggle);
};

export const getStatus = (properties: IItemProperty[]): number | null => {
  const statusProperty = getProperty(properties, WORKFLOW_STATE_MFILES_PROPERTY_ID);
  if (!statusProperty?.value) {
    return null;
  }

  return parseInt(statusProperty.value);
};

export const getHierarchyOfItems = (viewConfiguration: IViewConfigurationItem, allData: Array<IItem>): Array<IHierarchyItem> => {
  const allObjectTypes = Array.from(viewConfiguration.objectHierarchy.values()).map((otCfg) => otCfg.id);
  const hierarchy: IHierarchyItem[] = allData
    .filter((task) => allObjectTypes.includes(task.objectType))
    .map((item) => ({
      ...item,
      children: [],
      hierarchyLevel: -1,
      parentId: null,
    }));

  const hierarchyMap = new Map(hierarchy.map(i => [i.id, i]));
  const objIdToIdMap = new Map(hierarchy.map(i => [`${i.objectType}:${i.objectId}`, i.id]));
  const childMap = new Map<number, number[]>(hierarchy.map(i => [i.id, []]));

  for(const item of hierarchy) {
    for(const objectTypeHierarchyItem of viewConfiguration.objectHierarchy.values()) {
      for (const [childObjectType, parentProperty] of objectTypeHierarchyItem.childObjectTypes) {
        if(item.objectType === childObjectType) {
          const parentObjIds = getPropertyAsLookups(item.properties, parentProperty);
          for(const parentObjId of parentObjIds) {
            const parentId = objIdToIdMap.get(`${objectTypeHierarchyItem.id}:${parentObjId}`);
            if( typeof parentId !== 'undefined' && parentId !== item.id ) {
              const parentsChildren = childMap.get(parentId);
              parentsChildren?.push(item.id);
            }
          }
        }
      }
    }
  }

  for (const item of hierarchy) {
    // Set children to parent
    item.children = childMap.get(item.id);

    // Set parent to children
    for (const childId of item.children) {
      const child = hierarchyMap.get(childId);
      child.parentId = item.id;
    }
  }

  // Set hierarchylevel. Iterate over data. If:
  //    item has no parent -> hierarchyLevel = 0
  //    item has parent -> hierarchyLevel = parent level + 1
  //    item has parent, but parent hierarchyLevel < 0 -> continue
  // Continue until each item has hierarchyLevel. In practice iterate as many times as hierarchy is deep.
  const itemToHierarchyLevel: Record<number, number> = {};
  for (const item of hierarchy) {
    itemToHierarchyLevel[item.id] = -1;
  }
  let prevUnresolved = -1;
  while (Object.values(itemToHierarchyLevel).some((value) => value < 0)) {
    const unresolved = Object.values(itemToHierarchyLevel).filter(v => v < 0).length;
    if (unresolved === prevUnresolved) {
      break; // no progress → cycle in data, bail out
    }
    prevUnresolved = unresolved;
    for (const current of hierarchy) {
      if (current.parentId == null) {
        itemToHierarchyLevel[current.id] = 0;
        current.hierarchyLevel = 0;
      } else if (itemToHierarchyLevel[current.parentId] >= 0) {
        const parentLevel = itemToHierarchyLevel[current.parentId];
        itemToHierarchyLevel[current.id] = parentLevel + 1;
        current.hierarchyLevel = parentLevel + 1;
      }
    }
  }

  return hierarchy;
};

// Show only bottom level nodes that fulfill the filter condition + all their children
export const filterHierarchyRecursive = (
  data: IHierarchyItem[],
  filterFunc: (IHierarchyItem) => boolean,
  sortedByFunc: (IHierarchyItem) => number
): Array<IHierarchyItem> => {
  const sorted: IHierarchyItem[] = sortBy(data, sortedByFunc);

  const bottomLevelItems = sorted.filter((item) => item.parentId == null);
  const filteredBottomLevelItems = bottomLevelItems.filter(filterFunc).map((item) => item.id);
  return uniqBy(filteredBottomLevelItems.flatMap(i => getRecursiveWithChildren(data, i)), i => i.id)
};

const getRecursiveWithChildren = (data: IHierarchyItem[], id: number): IHierarchyItem[] => {
  const item = data.find(i => i.id === id);
  const items = item.children.flatMap(childId => getRecursiveWithChildren(data,childId));
  items.push(item);
  return items;
};

// Find configurations for adding new item
// Returns: error string or [parentEditConfig, childEditConfig]
export const getEditConfigForItemToAdd = (
  configuration: IConfiguration,
  parentItem: IItem | null
): string | [IEditObjectConfiguration, IEditObjectConfiguration] => {
  if (parentItem != null) {
    const parentEditConfiguration = configuration.objectTypeConfigurations.get(parentItem.objectType).editConfig;

    if (!parentEditConfiguration) {
      return `Invalid configuration: edit configuration for parent object type ${parentItem.objectType} missing`;
    }
    if (parentEditConfiguration.childObjectTypesAndProperties?.length <= 0) {
      return `No child object types configured for object type ${parentItem.objectType}`;
    }

    const [firstChildObjectType, _] = parentEditConfiguration.childObjectTypesAndProperties[0];
    const childEditConfiguration = configuration.objectTypeConfigurations.get(firstChildObjectType).editConfig;

    if (!childEditConfiguration) {
      return `Invalid configuration: edit configuration object type ${firstChildObjectType} missing`;
    }

    return [parentEditConfiguration, childEditConfiguration];
  } else {
    const firstObjectTypeConfiguration: IObjectTypeConfigurationItem = configuration.objectTypeConfigurations.values().next().value;
    return [null, firstObjectTypeConfiguration.editConfig];
  }
};
